/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * @file QuestionTopicsModal.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  QuestionTopicsDoneButton,
  QuestionTopicsModalStylesRoot,
  classes
} from './styles/QuestionTopicsModalStyles';
import AssignmentQuestionCard from './AssignmentQuestionCard';
import QuestionPreviewChapterSelect, {
  chapterOptionPropType
} from '../questionPreview/QuestionPreviewChapterSelect';
import { classes as questionPreviewClasses } from '../questionPreview/styles/QuestionPreviewStyles';

const viewPropType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  dueDateLabel: PropTypes.string.isRequired,
  dueDateFormatted: PropTypes.string,
  questionsTotalLabel: PropTypes.string.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  sections: PropTypes.arrayOf(chapterOptionPropType).isRequired,
  selectedSectionId: PropTypes.string,
  questions: PropTypes.array.isRequired,
  scheduleTocMenuContext: PropTypes.bool,
  textInfo: PropTypes.shape({
    sectionSelectLabel: PropTypes.string.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionsLabel: PropTypes.string.isRequired,
    noQuestionsMessage: PropTypes.string.isRequired,
    questionsPanelLabel: PropTypes.string.isRequired,
    doneLabel: PropTypes.string.isRequired,
    closeLabel: PropTypes.string.isRequired
  }).isRequired
});

const MetaChip = ({ label, value }) => (
  <div className={classes.metaChip}>
    {label}
    <span className={classes.metaChipValue}>{value}</span>
  </div>
);

MetaChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

/**
 * Questions & Topics modal: a section dropdown (flattened TOC sections, named exactly as in
 * the book) above the selected section's question cards.
 *
 * @param {object} view `{ title, ..., sections: [{ id, title, questionCount }], selectedSectionId, questions, textInfo }`
 * @param {Function} onSectionSelect called with the section id the user picked
 */
const QuestionTopicsModal = ({
  open,
  view,
  onClose,
  onDone,
  onSectionSelect
}) => {
  if (!open || !view) {
    return null;
  }

  const {
    title,
    dueDateLabel,
    dueDateFormatted,
    questionsTotalLabel,
    totalQuestions,
    sections,
    questions,
    textInfo,
    selectedSectionId,
    scheduleTocMenuContext
  } = view;

  const isScheduleTocMenuContext = Boolean(scheduleTocMenuContext);

  return (
    <QuestionTopicsModalStylesRoot
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <IconButton
        className={classes.closeButton}
        onClick={onClose}
        aria-label={textInfo.closeLabel}
      >
        <CloseIcon />
      </IconButton>

      <div className={classes.root}>
        <header className={classes.header}>
          {isScheduleTocMenuContext ? (
            <>
              <Typography component="h2" className={classes.sectionSelectHeaderLabel}>
                {textInfo.sectionSelectLabel}
              </Typography>
              <QuestionPreviewChapterSelect
                variant="section"
                label=""
                chapters={sections}
                selectedChapterId={selectedSectionId || ''}
                textInfo={textInfo}
                onChange={onSectionSelect}
                wrapClassName={questionPreviewClasses.sectionSelectWrapCompact}
              />
            </>
          ) : (
            <>
              <Typography component="h2" className={classes.assignmentTitle}>
                {title}
              </Typography>
              <div className={classes.metaRow}>
                <MetaChip label={dueDateLabel} value={dueDateFormatted || ''} />
                <MetaChip label={questionsTotalLabel} value={totalQuestions} />
              </div>
            </>
          )}
        </header>

        <div className={classes.body}>
          {!isScheduleTocMenuContext && (
            <QuestionPreviewChapterSelect
              variant="section"
              label={textInfo.sectionSelectLabel}
              chapters={sections}
              selectedChapterId={selectedSectionId || ''}
              textInfo={textInfo}
              onChange={onSectionSelect}
            />
          )}

          <section
            key={selectedSectionId}
            className={classes.questionsPane}
            aria-label={textInfo.questionsPanelLabel}
          >
            {questions.length ? (
              <ul className={classes.questionsList}>
                {questions.map((question) => (
                  <li key={question.questionId}>
                    <AssignmentQuestionCard
                      questionLabel={question.label}
                      questionStem={question.stem}
                      options={question.options}
                      defaultExpanded={question.defaultExpanded}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <Typography component="p" className={classes.emptyQuestions}>
                {textInfo.noQuestionsMessage}
              </Typography>
            )}
          </section>
        </div>

        <footer className={classes.footer}>
          <QuestionTopicsDoneButton type="button" onClick={onDone}>
            {textInfo.doneLabel}
          </QuestionTopicsDoneButton>
        </footer>
      </div>
    </QuestionTopicsModalStylesRoot>
  );
};

QuestionTopicsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  view: viewPropType,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onSectionSelect: PropTypes.func.isRequired
};

QuestionTopicsModal.defaultProps = {
  view: null
};

export default memo(QuestionTopicsModal);
